/**
 * Página de Minhas Consultas (Paciente)
 * Página protegida apenas para pacientes
 * Carrega agendamentos do backend
 */

import React, { useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  User,
  MessageCircle,
  Plus,
  AlertCircle,
  Loader2,
  Bell,
  Search,
  CheckCircle,
  XCircle,
  Navigation,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/hooks/useAuth';
import { useDoctorTracking } from '@/hooks/useDoctorTracking';
import { UserRole } from '@/types/auth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import { Input } from '@/components/ui/input';
import {
  getAppointmentsByPatient,
  getPatientByUserId,
  cancelAppointment,
} from '@/services/index';
import type { AppointmentDTO } from '@/services/index';
import { PatientRatingModal } from '@/components/consultation/PatientRatingModal';
import { ratingsService } from '@/services/ratings';
import { toast } from 'sonner';
import { formatDoctorSpecialty } from '@/lib/doctorSpecialty';
import {
  resolveAppointmentsLoadingView,
  shouldShowAppointmentsList,
  shouldShowFullPageLoader,
  shouldShowRefreshingIndicator,
} from '@/lib/appointmentsLoadingView';

// Lazy load Map component to avoid SSR issues if any
const LazyMap = React.lazy(() => import('@/components/tracking/LazyMap'));

export default function MinhasConsultasPage() {
  const navigate = useNavigate();
  const { user, isLoading: isAuthLoading, logout } = useAuth();
  const [appointments, setAppointments] = useState<AppointmentDTO[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement] = useState('');
  const fetchGenerationRef = useRef(0);

  // Rating states
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [selectedAppointmentForRating, setSelectedAppointmentForRating] =
    useState<AppointmentDTO | null>(null);
  const [pendingRatingIds, setPendingRatingIds] = useState<string[]>([]);

  const loadingView = useMemo(
    () =>
      resolveAppointmentsLoadingView({
        hasLoadedOnce,
        isFetching,
        appointmentsCount: appointments.length,
      }),
    [hasLoadedOnce, isFetching, appointments.length],
  );

  const inTransitIds = useMemo(
    () =>
      appointments
        .filter((appointment) => appointment.status === 'in_transit')
        .map((appointment) => appointment.id),
    [appointments],
  );
  const trackingData = useDoctorTracking(inTransitIds);

  // Check auth
  useEffect(() => {
    if (!isAuthLoading && !user) {
      navigate('/login');
    } else if (!isAuthLoading && user && user.role !== UserRole.PATIENT) {
      navigate('/dashboard');
    }
  }, [user, isAuthLoading, navigate]);

  // Load patient profile and appointments (mantém lista visível em refetch)
  const loadAppointments = useCallback(async () => {
    if (!user) return;

    const generation = ++fetchGenerationRef.current;
    setIsFetching(true);
    setError(null);

    try {
      const patientResponse = await getPatientByUserId(user.id);
      if (generation !== fetchGenerationRef.current) return;

      if (patientResponse.success && patientResponse.data) {
        const patientId = patientResponse.data.userId;
        const response = await getAppointmentsByPatient(patientId);
        if (generation !== fetchGenerationRef.current) return;

        if (response.success) {
          const appointmentsData = Array.isArray(response.data) ? response.data : [];
          setAppointments(appointmentsData);
        } else {
          throw new Error(response.error || 'Erro ao carregar consultas');
        }

        const pendingResponse = await ratingsService.getPendingRatings();
        if (generation !== fetchGenerationRef.current) return;

        if (pendingResponse.success && pendingResponse.data) {
          setPendingRatingIds(pendingResponse.data.map((rating: { id: string }) => rating.id));
        }
      }
    } catch (err) {
      if (generation !== fetchGenerationRef.current) return;
      console.error('Error loading appointments:', err);
      setError('Não foi possível carregar suas consultas. Tente novamente mais tarde.');
    } finally {
      if (generation === fetchGenerationRef.current) {
        setIsFetching(false);
        setHasLoadedOnce(true);
      }
    }
  }, [user]);

  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments]);

  // Poll leve: quando o médico inicia deslocamento, o paciente vê o status sem precisar F5
  useEffect(() => {
    if (!user || user.role !== UserRole.PATIENT) return;

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void loadAppointments();
      }
    }, 12000);

    return () => window.clearInterval(intervalId);
  }, [user, loadAppointments]);

  // Revalida ao voltar à aba (evita status de remarcação fantasma após ação do médico)
  useEffect(() => {
    const onFocus = () => {
      if (user) void loadAppointments();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && user) void loadAppointments();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, loadAppointments]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleRatingClick = (appointment: AppointmentDTO) => {
    setSelectedAppointmentForRating(appointment);
    setIsRatingModalOpen(true);
  };

  const handleScheduleClick = () => {
    navigate('/buscar-medicos');
  };

  const handleCancelClick = async (appointmentId: string) => {
    if (window.confirm('Tem certeza que deseja cancelar esta consulta?')) {
      try {
        const response = await cancelAppointment(appointmentId);
        if (response.success) {
          toast.success('Consulta cancelada com sucesso');
          void loadAppointments();
        } else {
          toast.error(response.error || 'Erro ao cancelar consulta');
        }
      } catch {
        toast.error('Erro ao cancelar consulta');
      }
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, { label: string; color: string; icon: LucideIcon }> = {
      requested: {
        label: 'Aguardando Aprovação',
        color: 'amber',
        icon: Clock,
      },
      scheduled: {
        label: 'Confirmada',
        color: 'blue',
        icon: CheckCircle,
      },
      reschedule_requested: {
        label: 'Reagendamento Solicitado',
        color: 'purple',
        icon: Clock,
      },
      completed: {
        label: 'Realizada',
        color: 'green',
        icon: CheckCircle,
      },
      cancelled: {
        label: 'Cancelada',
        color: 'red',
        icon: XCircle,
      },
      'no-show': {
        label: 'Não Compareceu',
        color: 'amber',
        icon: AlertCircle,
      },
      in_transit: {
        label: 'Médico em Deslocamento',
        color: 'indigo',
        icon: Navigation,
      },
    };
    return labels[status] || labels.scheduled;
  };

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      {/* ARIA Live Region for dynamic status announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only" role="status">
        {announcement}
        {shouldShowRefreshingIndicator(loadingView) ? 'Atualizando consultas' : ''}
      </div>

      {/* Sidebar */}
      <Sidebar userRole={user.role} userName={user.name} onLogout={handleLogout} />

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Minhas Consultas</h1>
              {shouldShowRefreshingIndicator(loadingView) && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-teal-700 dark:text-teal-300 bg-teal-50 dark:bg-teal-900/30 border border-teal-100 dark:border-teal-800 px-2.5 py-1 rounded-full"
                  aria-live="polite"
                >
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Atualizando…
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Visualize e gerencie seus agendamentos
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative hidden md:block w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar consultas..."
                className="pl-10 border-gray-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-white"
              />
            </div>

            <button className="p-2 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-colors">
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>

            <ThemeToggle />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-8 bg-gray-50 dark:bg-neutral-950">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Action Button */}
          <div className="mb-6 flex gap-3">
            <button
              onClick={handleScheduleClick}
              className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:hover:bg-teal-500 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Agendar Consulta
            </button>
          </div>

          {/* Appointments List — lista permanece montada em refetch */}
          {shouldShowFullPageLoader(loadingView) ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-teal-600 animate-spin mr-2" />
              <p className="text-gray-600 dark:text-gray-400">Carregando consultas...</p>
            </div>
          ) : shouldShowAppointmentsList(loadingView) ? (
            <div className="grid gap-4">
              {appointments.map((appointment) => {
                const statusInfo = getStatusLabel(appointment.status);
                const StatusIcon = statusInfo.icon;

                const getDisplayDate = () => {
                  try {
                    let date: Date | null = null;
                    if (appointment.appointmentDate) {
                      date = parseISO(appointment.appointmentDate);
                    } else if (appointment.date) {
                      date = parseISO(appointment.date);
                    }

                    if (date && isValid(date)) {
                      return format(date, 'dd/MM/yyyy', { locale: ptBR });
                    }
                    return 'Data indisponível';
                  } catch {
                    return 'Data inválida';
                  }
                };

                const getDisplayTime = () => {
                  try {
                    if (
                      appointment.time &&
                      appointment.time !== 'undefined:undefined' &&
                      appointment.time !== ''
                    ) {
                      return appointment.time;
                    }

                    if (appointment.appointmentDate) {
                      const date = parseISO(appointment.appointmentDate);
                      if (date && isValid(date)) {
                        return format(date, 'HH:mm');
                      }
                    }
                    return 'Horário a definir';
                  } catch {
                    return 'Horário inválido';
                  }
                };

                return (
                  <div
                    key={appointment.id}
                    className="bg-white dark:bg-neutral-900 rounded-lg p-6 border border-gray-200 dark:border-neutral-800 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <User className="w-5 h-5 text-gray-400" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {appointment.doctor?.user?.name || 'Médico'}
                          </h3>
                          <span className="px-2 py-1 bg-gray-100 dark:bg-neutral-800 text-xs font-medium text-gray-700 dark:text-gray-300 rounded">
                            {formatDoctorSpecialty(appointment.doctor?.specialty) || 'Especialidade'}
                          </span>
                        </div>

                        <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {getDisplayDate()}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            {getDisplayTime()}
                          </div>
                        </div>

                        {appointment.notes && (
                          <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                            <strong>Observações:</strong> {appointment.notes}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 items-end">
                        <div
                          className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                            statusInfo.color === 'blue'
                              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                              : statusInfo.color === 'green'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                : statusInfo.color === 'red'
                                  ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                  : statusInfo.color === 'indigo'
                                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                                    : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                          }`}
                        >
                          <StatusIcon className="w-4 h-4" />
                          {statusInfo.label}
                        </div>

                        {(statusInfo.color === 'blue' || statusInfo.color === 'indigo') && (
                          <div className="flex flex-col gap-2 mt-2">
                            <button
                              onClick={() => navigate(`/chat/${appointment.id}`)}
                              className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                            >
                              <MessageCircle className="w-4 h-4" />
                              Abrir Chat
                            </button>
                            {statusInfo.color === 'blue' && (
                              <button
                                onClick={() =>
                                  navigate(`/agendamento?reschedule=${appointment.id}`)
                                }
                                className="w-full text-sm px-3 py-1 border border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400 rounded hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                              >
                                Reagendar
                              </button>
                            )}
                          </div>
                        )}

                        {appointment.status === 'completed' &&
                          pendingRatingIds.includes(appointment.id) && (
                            <div className="flex flex-col gap-2 mt-2">
                              <button
                                onClick={() => handleRatingClick(appointment)}
                                className="w-full inline-flex items-center justify-center gap-2 text-sm px-3 py-1 bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors"
                              >
                                Avaliar Consulta
                              </button>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* Tracking Data visualization */}
                    {appointment.status === 'in_transit' && trackingData[appointment.id] && (
                      <div className="mt-4 flex flex-col gap-2">
                        <div className="p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800 rounded flex items-center gap-3">
                          <MapPin className="w-5 h-5 text-indigo-600 dark:text-indigo-400 animate-pulse flex-shrink-0" />
                          <div className="text-sm text-indigo-800 dark:text-indigo-300">
                            <strong>Localização atualizada:</strong>
                            <span className="ml-2 font-mono">
                              Lat: {trackingData[appointment.id].latitude.toFixed(5)}, Lng:{' '}
                              {trackingData[appointment.id].longitude.toFixed(5)}
                            </span>
                          </div>
                        </div>

                        <Suspense
                          fallback={
                            <div className="w-full h-64 bg-gray-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center border border-gray-200 dark:border-neutral-800 animate-pulse mt-3">
                              <div className="flex flex-col items-center gap-2">
                                <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  Carregando script do mapa...
                                </span>
                              </div>
                            </div>
                          }
                        >
                          <LazyMap
                            latitude={trackingData[appointment.id].latitude}
                            longitude={trackingData[appointment.id].longitude}
                          />
                        </Suspense>
                      </div>
                    )}

                    {appointment.status === 'in_transit' && !trackingData[appointment.id] && (
                      <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-100 dark:border-indigo-800 rounded flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400 animate-spin flex-shrink-0" />
                        <p className="text-sm text-indigo-800 dark:text-indigo-300">
                          Aguardando a localização do médico em deslocamento…
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white dark:bg-neutral-900 rounded-lg p-12 text-center border border-gray-200 dark:border-neutral-800 border-dashed">
              <Calendar className="w-12 h-12 text-gray-300 dark:text-neutral-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Nenhuma consulta agendada
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Você não possui consultas registradas. Clique no botão acima para agendar uma.
              </p>
              <button
                onClick={handleScheduleClick}
                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Agendar Primeira Consulta
              </button>
            </div>
          )}
        </main>

        {/* Patient Rating Modal */}
        {selectedAppointmentForRating && (
          <PatientRatingModal
            isOpen={isRatingModalOpen}
            onClose={() => {
              setIsRatingModalOpen(false);
              setSelectedAppointmentForRating(null);
            }}
            appointmentId={selectedAppointmentForRating.id}
            doctorName={selectedAppointmentForRating.doctor?.user?.name || 'Médico'}
            onSuccess={loadAppointments}
          />
        )}
      </div>
    </div>
  );
}
